import React, { useEffect, useState } from "react"
import ProfileTabs from "../components/ProfileTabs"
import ProfileTitle from "../components/ProfileTitle"
import { useAuth } from "../context/authContext"
import { applyPriceMultiplier, getMyStores, getShopifyProductsBySellerId, setShopifyProductVisibility, setStorePriceMultiplier } from "../firebase/shopify"
import { getCategoryById } from "../firebase/categories"
import DialogEditShopifyCategory from "../components/dialogs/DialogEditShopifyCategory"
import DialogBulkShopifyCategory from "../components/dialogs/DialogBulkShopifyCategory"
import DialogShopifyCategoryMapping from "../components/dialogs/DialogShopifyCategoryMapping"

const CATEGORY_SOURCE_LABELS = { mapping: "Auto", manual: "Manual" }

// The owner's enable/disable switch for one listing. Optimistic: the row
// flips immediately and rolls back if the write fails, so toggling several
// listings in a row doesn't stall behind a round trip each time.
const VisibilitySwitch = ({ enabled, busy, onChange }) => (
    <button
        type="button"
        role="switch"
        aria-checked={enabled}
        aria-label={enabled ? 'Listed on the marketplace' : 'Hidden from the marketplace'}
        disabled={busy}
        onClick={() => onChange(!enabled)}
        className={`relative inline-flex items-center w-[42px] h-[22px] rounded-full transition-colors ${enabled ? 'bg-primary' : 'bg-[#4a4a4a]'} ${busy ? 'opacity-50 cursor-wait' : 'cursor-pointer'}`}
    >
        <span
            className={`inline-block w-[16px] h-[16px] bg-white rounded-full transition-transform ${enabled ? 'translate-x-[23px]' : 'translate-x-[3px]'}`}
        />
    </button>
)

// One store's markup. Kept as a string while editing so a half-typed "1."
// isn't snapped back to a number under the owner's cursor.
const StorePricingRow = ({ store, onSaved }) => {
    const [value, setValue] = useState(String(store.price_multiplier ?? 1))
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState("")

    const parsed = Number(value)
    const valid = Number.isFinite(parsed) && parsed >= 0.01 && parsed <= 100
    const dirty = valid && parsed !== Number(store.price_multiplier ?? 1)

    const handleSave = async () => {
        if (!valid || saving) return
        setSaving(true)
        setError("")
        try {
            const result = await setStorePriceMultiplier(store.shop_domain, parsed)
            onSaved(store.shop_domain, result.price_multiplier)
            setValue(String(result.price_multiplier))
        } catch (err) {
            console.error("Failed to save price multiplier:", err)
            setError(err.message || "Failed to save")
        } finally {
            setSaving(false)
        }
    }

    return (
        <div className="border-[1px] border-gray px-4 py-3 flex flex-wrap items-center gap-4 justify-between">
            <div className="min-w-0">
                <div className="text-white text-sm truncate">{store.shop_domain}</div>
                <div className="text-[#717171] text-xs mt-1">
                    {/* The example is what makes the multiplier legible at a
                        glance -- "1.2" on its own says little. */}
                    A $10.00 item sells for ${applyPriceMultiplier(10, valid ? parsed : 1).toFixed(2)}
                </div>
            </div>

            <div className="flex items-center gap-3">
                <label className="text-[#717171] text-xs uppercase">Price multiplier</label>
                <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    max="100"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    className="bg-[#2E2E2E] border-[1px] border-gray text-white px-3 py-2 w-[100px] text-sm"
                />
                <button
                    className="solo-button text-white border-[1px] border-gray px-4 py-2 text-xs disabled:opacity-40"
                    onClick={handleSave}
                    disabled={!dirty || saving}
                >
                    {saving ? "Saving..." : "Save"}
                </button>
            </div>

            {!valid && <span className="text-red-500 text-xs w-full">Enter a number between 0.01 and 100</span>}
            {error && <span className="text-red-500 text-xs w-full">{error}</span>}
        </div>
    )
}

const ShopifyListingRow = ({ item, selected, onSelect, onEdit, onVisibilityChange }) => {
    const [category, setCategory] = useState(null)
    const [savingVisibility, setSavingVisibility] = useState(false)

    const listed = !item.hidden

    const handleToggle = async (nextListed) => {
        const previous = !!item.hidden
        setSavingVisibility(true)
        // Flip the row first, then reconcile: a rejected write restores the
        // previous state rather than leaving the switch lying about it.
        onVisibilityChange(item.id, !nextListed)
        try {
            await setShopifyProductVisibility(item.id, !nextListed)
        } catch (error) {
            console.error('Failed to update listing visibility:', error)
            onVisibilityChange(item.id, previous)
            alert(error.message || 'Failed to update listing visibility')
        } finally {
            setSavingVisibility(false)
        }
    }

    useEffect(() => {
        // A listing not yet assigned a real category holds the literal
        // string 'shopify' here, which resolves to no category doc -- that's
        // expected, it just means "uncategorized" below.
        if (item.category && item.category !== 'shopify') {
            getCategoryById(item.category).then(setCategory)
        } else {
            setCategory(null)
        }
    }, [item.category])

    return (
        <tr className={`border-[1px] border-gray text-nowrap ${listed ? '' : 'opacity-50'} ${selected ? 'bg-[#1d2a30]' : ''}`}>
            <td className="py-2 pl-3 pr-2 w-[40px]">
                <input
                    type="checkbox"
                    className="w-4 h-4 accent-[#1AB6F9] cursor-pointer"
                    checked={selected}
                    onChange={(e) => onSelect(item.id, e.target.checked)}
                    aria-label={`Select ${item.title}`}
                />
            </td>

            {/* Titles are merchant data and run long, and the row is
                text-nowrap, so one product name used to push Visibility and
                Actions off the right edge. The cap sits on the inner div --
                a max-width on the <td> is ignored under auto table layout --
                and the full title stays available on hover. */}
            <td className="py-2 pl-3 pr-6">
                <div className="flex items-center w-[180px] lg:w-[240px]">
                    <img
                        src={item.pictures[0]}
                        alt="picture"
                        className="w-[50px] h-[50px] mr-3 shrink-0 object-cover bg-white border-[1px] border-gray"
                    />
                    <span className="uppercase text-sm truncate min-w-0" title={item.title}>
                        {item.title}
                    </span>
                </div>
            </td>

            <td className="lg:min-w-[120px] pr-10">
                $ {Number(item.price || 0).toFixed(2)}
                {/* Only worth showing when the two differ; at 1x it would
                    just be the same number twice. */}
                {Number(item.price_multiplier || 1) !== 1 && (
                    <div className="text-[#717171] text-xs">
                        ${Number(item.base_price || 0).toFixed(2)} &times; {item.price_multiplier}
                    </div>
                )}
            </td>

            <td className="lg:min-w-[120px] pr-10">{item.quantity}</td>

            <td className="lg:min-w-[160px] pr-10" title={item.shopify_category?.full_name || ""}>
                {item.shopify_category?.name || <span className="text-[#717171]">-</span>}
            </td>

            <td className="lg:min-w-[180px] pr-10">
                {category ? category.name : <span className="text-[#717171]">Uncategorized</span>}
                {category && CATEGORY_SOURCE_LABELS[item.category_source] && (
                    <span className="ml-2 text-xs text-[#717171]">({CATEGORY_SOURCE_LABELS[item.category_source]})</span>
                )}
            </td>

            <td className="lg:min-w-[180px] pr-10">
                {item.sub_category && item.sub_category !== 'shopify' ? item.sub_category : '-'}
            </td>

            <td className="lg:min-w-[150px] pr-10">
                <div className="flex items-center gap-3">
                    <VisibilitySwitch enabled={listed} busy={savingVisibility} onChange={handleToggle} />
                    <span className={`text-xs uppercase ${listed ? 'text-primary' : 'text-[#717171]'}`}>
                        {listed ? 'Listed' : 'Hidden'}
                    </span>
                </div>
            </td>

            <td className="pr-3">
                <button
                    className="solo-button text-white border-[1px] border-gray px-4 py-2 text-xs"
                    onClick={() => onEdit(item)}
                >
                    Edit Category
                </button>
            </td>
        </tr>
    )
}

const ShopifyListingsPage = () => {
    const { user } = useAuth()
    const [items, setItems] = useState([])
    const [loading, setLoading] = useState(true)
    const [editingItem, setEditingItem] = useState(null)
    const [mappingOpen, setMappingOpen] = useState(false)
    const [bulkOpen, setBulkOpen] = useState(false)
    const [stores, setStores] = useState([])
    // Ids rather than rows: the rows are replaced on every in-place update,
    // and stale copies of them would drift from what is on screen.
    const [selectedIds, setSelectedIds] = useState([])

    const refresh = () => {
        if (!user?.uid) return
        setLoading(true)
        setSelectedIds([])
        getShopifyProductsBySellerId(user.uid).then((data) => {
            setItems(data)
            setLoading(false)
        })
        getMyStores(user.uid, { refresh: true }).then(setStores)
    }

    // A multiplier change reprices every listing of that store, so the rows
    // are updated in place rather than left showing the old price until the
    // next load.
    const applyMultiplier = (shopDomain, multiplier) => {
        setStores((prev) => prev.map((store) => (
            store.shop_domain === shopDomain ? { ...store, price_multiplier: multiplier } : store
        )))
        setItems((prev) => prev.map((item) => (
            item.shop_domain === shopDomain
                ? { ...item, price_multiplier: multiplier, price: applyPriceMultiplier(item.base_price, multiplier) }
                : item
        )))
    }

    const applyVisibility = (id, hidden) => {
        setItems((prev) => prev.map((item) => (item.id === id ? { ...item, hidden } : item)))
    }

    /**
     * Writes a saved category onto the rows it was applied to.
     *
     * Refetching instead is what made categorising a catalogue painful: the
     * table was swapped for a loading line, so the seller lost their place
     * in a long list after every single save and had to scroll back down.
     *
     * sub_category_assigned has to be kept in step with sub_category here,
     * because the browse filter reads it to tell a listing with no
     * sub-category from one whose sub_category is only a Shopify product
     * type -- see utils/listingFilters.
     */
    const applyCategory = (ids, category, subCategory) => {
        const assigned = Boolean(String(subCategory || '').trim())
        setItems((prev) => prev.map((item) => (ids.includes(item.id)
            ? {
                ...item,
                category,
                sub_category: subCategory || item.product_type || 'shopify',
                sub_category_assigned: assigned,
                category_source: 'manual'
            }
            : item
        )))
    }

    const selectedSet = new Set(selectedIds)
    const selectedItems = items.filter((item) => selectedSet.has(item.id))
    const allSelected = items.length > 0 && selectedIds.length === items.length

    const toggleSelected = (id, checked) => {
        setSelectedIds((prev) => (checked ? [...prev, id] : prev.filter((entry) => entry !== id)))
    }

    const toggleSelectAll = (checked) => {
        setSelectedIds(checked ? items.map((item) => item.id) : [])
    }

    useEffect(() => {
        refresh()
    }, [user?.uid])

    return (
        <div>
            <ProfileTitle />

            <ProfileTabs activeTab="Shopify Listings" />

            {stores.length > 0 && (
                <div className="mb-6 space-y-2">
                    <div className="text-white text-sm uppercase">Store pricing</div>
                    {stores.map((store) => (
                        <StorePricingRow key={store.shop_domain} store={store} onSaved={applyMultiplier} />
                    ))}
                </div>
            )}

            {!loading && items.length > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    {/* Holds the row's height whether or not anything is
                        selected, so the table doesn't jump as the seller
                        ticks the first box. */}
                    <div className="text-xs text-[#717171] min-h-[30px] flex items-center gap-3">
                        {selectedIds.length > 0 ? (
                            <>
                                <span className="text-white">
                                    {selectedIds.length} selected
                                </span>
                                <button
                                    className="solo-button text-white border-white border-[1px] bg-primary px-4 py-2 text-xs"
                                    onClick={() => setBulkOpen(true)}
                                >
                                    Set Category
                                </button>
                                <button
                                    className="underline"
                                    onClick={() => setSelectedIds([])}
                                >
                                    Clear
                                </button>
                            </>
                        ) : (
                            <span>Tick listings to set their category together</span>
                        )}
                    </div>

                    <button
                        className="solo-button text-white border-[1px] border-gray px-4 py-2 text-xs"
                        onClick={() => setMappingOpen(true)}
                    >
                        Category Mapping
                    </button>
                </div>
            )}

            {loading ? (
                <div className="text-white text-lg text-center mt-8">Loading Shopify listings...</div>
            ) : items.length > 0 ? (
                <div className="w-full overflow-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-[#2E2E2E] border-[1px] border-gray py-3 text-sm text-[#717171]">
                                <th className="py-3 pl-3 text-left w-[40px]">
                                    <input
                                        type="checkbox"
                                        className="w-4 h-4 accent-[#1AB6F9] cursor-pointer"
                                        checked={allSelected}
                                        onChange={(e) => toggleSelectAll(e.target.checked)}
                                        aria-label="Select all listings"
                                    />
                                </th>
                                <th className="py-3 text-left">Product</th>
                                <th className="py-3 text-left">Price</th>
                                <th className="py-3 text-left">Inventory</th>
                                <th className="py-3 text-left">Shopify Category</th>
                                <th className="py-3 text-left">Category</th>
                                <th className="py-3 text-left">Sub-Category</th>
                                <th className="py-3 text-left" title="Hidden listings are removed from Find Cards and can't be bought; existing orders are unaffected">Visibility</th>
                                <th className="py-3 text-left">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="text-white space-y-1 text-sm">
                            {items.map((item) => (
                                <ShopifyListingRow
                                    key={item.id}
                                    item={item}
                                    selected={selectedSet.has(item.id)}
                                    onSelect={toggleSelected}
                                    onEdit={setEditingItem}
                                    onVisibilityChange={applyVisibility}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="text-white text-lg text-center mt-8">
                    No Shopify listings found for your account
                </div>
            )}

            <DialogEditShopifyCategory
                open={!!editingItem}
                item={editingItem}
                onClose={() => setEditingItem(null)}
                onSaved={(saved) => {
                    // A reset ("use category mapping") resolves server-side,
                    // so only a saved assignment can be applied in place --
                    // otherwise fall back to a reload.
                    if (saved?.category) {
                        applyCategory([editingItem.id], saved.category, saved.subCategory)
                    } else {
                        refresh()
                    }
                    setEditingItem(null)
                }}
            />

            <DialogBulkShopifyCategory
                open={bulkOpen}
                items={selectedItems}
                onClose={() => setBulkOpen(false)}
                onSaved={({ category, subCategory }) => {
                    applyCategory(selectedIds, category, subCategory)
                    setSelectedIds([])
                    setBulkOpen(false)
                }}
            />

            <DialogShopifyCategoryMapping
                open={mappingOpen}
                onClose={() => setMappingOpen(false)}
                onSaved={() => {
                    setMappingOpen(false)
                    refresh()
                }}
            />
        </div>
    )
}

export default ShopifyListingsPage
